/**
 * React Query hook for user notes
 * Provides access to personal notes with filtering and search
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  listNotes,
  getNote,
  createNote,
  updateNote,
  deleteNote,
  restoreNote,
  toggleNotePin,
  toggleNoteArchive,
  searchNotes,
  updateNoteChecklist,
  promoteChecklistItemToTask,
} from '@/services/notepad/notes.service';
import { logger } from "@/lib/logger";
import { Note, NoteInput, NoteQueryOptions, NoteChecklistItem } from '@/types/notepad';
import { notepadToast } from '@/lib/toast/notepadToast';

/**
 * Query keys factory for notes
 */
export const noteKeys = {
  all: (userId: string) => ['notes', userId] as const,
  lists: (userId: string) => [...noteKeys.all(userId), 'list'] as const,
  list: (userId: string, options?: NoteQueryOptions) =>
    [...noteKeys.lists(userId), options] as const,
  search: (userId: string, searchText: string, options?: NoteQueryOptions) =>
    [...noteKeys.all(userId), 'search', searchText, options] as const,
  details: (userId: string) => [...noteKeys.all(userId), 'detail'] as const,
  detail: (userId: string, noteId: string) => [...noteKeys.details(userId), noteId] as const,
};

/**
 * Hook to list user's notes with optional filters
 * Auto-cancels when user logs out
 */
export function useMyNotes(options: NoteQueryOptions = {}) {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: noteKeys.list(userId || '', options),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return listNotes(userId, options);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Hook to search notes
 */
export function useSearchNotes(searchText: string, options: NoteQueryOptions = {}) {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: noteKeys.search(userId || '', searchText, options),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return searchNotes(userId, searchText, options);
    },
    enabled: !!userId && searchText.length > 0,
    staleTime: 1000 * 60 * 1, // 1 minute for search results
  });
}

/**
 * Hook to get a single note
 */
export function useNote(noteId: string) {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: noteKeys.detail(userId || '', noteId),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return getNote(userId, noteId);
    },
    enabled: !!userId && !!noteId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Hook to create a new note
 */
export function useCreateNote() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: NoteInput) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return createNote(userId, input);
    },
    onSuccess: (newNote) => {
      // Invalidate all note lists
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });

      // Optimistically add to cache
      queryClient.setQueryData<Note[]>(
        noteKeys.list(userId || '', {}),
        (old) => (old ? [newNote, ...old] : [newNote])
      );

      notepadToast.noteCreated();
    },
    onError: (error: Error) => {
      logger.error('[useCreateNote] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to update a note
 */
export function useUpdateNote() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, input }: { noteId: string; input: Partial<NoteInput> }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return updateNote(userId, noteId, input);
    },
    onSuccess: (updatedNote) => {
      // Invalidate lists and detail
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      queryClient.setQueryData(noteKeys.detail(userId || '', updatedNote.id), updatedNote);

      notepadToast.noteUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useUpdateNote] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to update note checklist
 */
export function useUpdateNoteChecklist() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      checklist,
    }: {
      noteId: string;
      checklist: NoteChecklistItem[];
    }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return updateNoteChecklist(userId, noteId, checklist);
    },
    onSuccess: (updatedNote) => {
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      queryClient.setQueryData(noteKeys.detail(userId || '', updatedNote.id), updatedNote);
    },
    onError: (error: Error) => {
      logger.error('[useUpdateNoteChecklist] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to toggle note pin status
 */
export function useToggleNotePin() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return toggleNotePin(userId, noteId);
    },
    onSuccess: (updatedNote) => {
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      queryClient.setQueryData(noteKeys.detail(userId || '', updatedNote.id), updatedNote);

      if (updatedNote.pinned) {
        notepadToast.notePinned();
      } else {
        notepadToast.noteUnpinned();
      }
    },
    onError: (error: Error) => {
      logger.error('[useToggleNotePin] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to toggle note archive status
 */
export function useToggleNoteArchive() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return toggleNoteArchive(userId, noteId);
    },
    onSuccess: (updatedNote) => {
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      queryClient.setQueryData(noteKeys.detail(userId || '', updatedNote.id), updatedNote);

      if (updatedNote.archived) {
        notepadToast.noteArchived();
      } else {
        notepadToast.noteUnarchived();
      }
    },
    onError: (error: Error) => {
      logger.error('[useToggleNoteArchive] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to delete a note
 */
export function useDeleteNote() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return deleteNote(userId, noteId);
    },
    onMutate: async (noteId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: noteKeys.lists(userId || '') });

      // Snapshot previous value
      const previousNotes = queryClient.getQueriesData({ queryKey: noteKeys.lists(userId || '') });

      // Optimistically remove note from all lists
      queryClient.setQueriesData<Note[]>(
        { queryKey: noteKeys.lists(userId || '') },
        (old) => old?.filter((note) => note.id !== noteId) ?? []
      );

      return { previousNotes };
    },
    onError: (error: Error, noteId, context) => {
      // Rollback on error
      if (context?.previousNotes) {
        context.previousNotes.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('[useDeleteNote] error:', error);
      notepadToast.noteError(error);
    },
    onSettled: () => {
      // Refetch after mutation
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
    },
  });
}

/**
 * Hook to restore a deleted note
 */
export function useRestoreNote() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return restoreNote(userId, noteId);
    },
    onSuccess: (restoredNote) => {
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      queryClient.setQueryData(noteKeys.detail(userId || '', restoredNote.id), restoredNote);
      notepadToast.noteUpdated();
    },
    onError: (error: Error) => {
      logger.error('[useRestoreNote] error:', error);
      notepadToast.noteError(error);
    },
  });
}

/**
 * Hook to promote a checklist item to a task
 */
export function usePromoteChecklistItemToTask() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ noteId, itemId }: { noteId: string; itemId: string }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return promoteChecklistItemToTask(userId, noteId, itemId);
    },
    onSuccess: ({ note, taskId }) => {
      // Invalidate both notes and tasks
      void void queryClient.invalidateQueries({ queryKey: noteKeys.lists(userId || '') });
      void void queryClient.invalidateQueries({ queryKey: ['tasks', userId || ''] });
      queryClient.setQueryData(noteKeys.detail(userId || '', note.id), note);

      notepadToast.noteUpdated();
    },
    onError: (error: Error) => {
      logger.error('[usePromoteChecklistItemToTask] error:', error);
      notepadToast.noteError(error);
    },
  });
}
